import { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTicketsContext } from '../../context/TicketContext';
import { formatDate } from '../../utils/dateUtils';

export default function TicketList() {
  const { token, user } = useAuth();
  const { tickets, loading, error, dispatch } = useTicketsContext();

  const [filteredTickets, setFilteredTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [creatorFilter, setCreatorFilter] = useState('All');
  const [monthFilter, setMonthFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [creators, setCreators] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);

  // Purpose: Sets up creators, months, and years for filter dropdowns.
  // Runs when: tickets in context is updated.
  useEffect(() => {
    if (!tickets.length) return;

    setFilteredTickets(tickets);

    const uniqueCreators = [...new Set(tickets.map(ticket => ticket.createdBy?.name || 'Unknown'))];
    setCreators(uniqueCreators);

    const monthSet = new Set();
    const yearSet = new Set();
    tickets.forEach(ticket => {
      const date = new Date(ticket.createdAt);
      monthSet.add(date.getMonth());
      yearSet.add(date.getFullYear());
    });
    setMonths([...monthSet]);
    setYears([...yearSet]);
  }, [tickets]);

  // Purpose: Applies search and filter conditions.
  // Runs when: Any filter, search term, or ticket list changes.
  useEffect(() => {
    let updated = tickets;
    if (statusFilter !== 'All') {
      updated = updated.filter(ticket => ticket.status === statusFilter);
    }
    if (priorityFilter !== ' ' && priorityFilter !== 'All') {
      updated = updated.filter(ticket => ticket.priority === priorityFilter);
    }
    if ((user?.role === 'IT' || user?.role === 'Executive') && creatorFilter !== 'All') {
      updated = updated.filter(ticket => (ticket.createdBy?.name || 'Unknown') === creatorFilter);
    }
    if (monthFilter !== 'All') {
      updated = updated.filter(ticket => new Date(ticket.createdAt).getMonth().toString() === monthFilter);
    }
    if (yearFilter !== 'All') {
      updated = updated.filter(ticket => new Date(ticket.createdAt).getFullYear().toString() === yearFilter);
    }
    if (searchTerm.trim()) {
      updated = updated.filter(ticket => ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    setFilteredTickets(updated);
  }, [statusFilter, priorityFilter, creatorFilter, monthFilter, yearFilter, searchTerm, tickets, user]);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await api.get('/api/tickets', {
          headers: { Authorization: `Bearer ${token}` },
        });
        dispatch({ type: 'FETCH_SUCCESS', payload: res.data });
      } catch (err) {
        console.error('Failed to load tickets:', err.message);
      }
    };

    fetchTickets();
  }, [dispatch, token]);


  const handleDelete = async (id) => {
    if (user?.role !== 'IT') return;
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      await api.delete(`/api/tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch({ type: 'DELETE_TICKET', payload: id });
    } catch (err) {
      alert('Delete failed');
    }
  };

  if (!token) return <p>Please log in to view tickets.</p>;
  if (loading) return <p>Loading tickets...</p>;

  /* const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('default', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }; */

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="container mt-4">
      <h2>Your Tickets ({filteredTickets.length})</h2>
      {error && <p className="text-danger">{error}</p>}

      <div className="mb-3 d-flex flex-wrap gap-3">
        <div>
          <label htmlFor="statusFilter" className="form-label">Status:</label>
          <select id="statusFilter" className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All</option>
            <option>Open</option>
            <option>In Progress</option>
            <option>Closed</option>
            <option>Canceled</option>
            <option>Waiting for Approval</option>
          </select>
        </div>

        <div>
          <label htmlFor="priorityFilter" className="form-label">Priority:</label>
          <select id="priorityFilter" className="form-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="All">All</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </div>

        {(user?.role === 'IT' || user?.role === 'Executive') && (
          <div>
            <label htmlFor="creatorFilter" className="form-label">Created By:</label>
            <select id="creatorFilter" className="form-select" value={creatorFilter} onChange={(e) => setCreatorFilter(e.target.value)}>
              <option value="All">All</option>
              {creators.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="monthFilter" className="form-label">Month:</label>
          <select id="monthFilter" className="form-select" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="All">All</option>
            {months.map(m => (
              <option key={m} value={m}>{monthNames[m]}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="yearFilter" className="form-label">Year:</label>
          <select id="yearFilter" className="form-select" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
            <option value="All">All</option>
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="search" className="form-label">Search Subject:</label>
          <input type="text" id="search" className="form-control" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." />
        </div>
      </div>

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>#</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Priority</th>
            {(user?.role === 'IT' || user?.role === 'Executive') && (
              <th>Created By</th>
            )}
            <th>Created At</th>              
            <th>Last Update</th>
            <th>Handled by</th>
            {/* <th>Remark</th> */}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTickets.map((ticket, index) => (
            <tr key={ticket._id}>
              <td>{index + 1}</td>
              {/* <td>{ticket.subject}</td> */}
              <td>{ticket.subject ? `${ticket.subject.split(' ').slice(0, 5).join(' ')} ...` : '—'}</td>
              <td>
                <span className={
                  ticket.status === 'Open' ? 'text-danger'
                  : ticket.status === 'In Progress' ? 'text-warning'
                  : ticket.status === 'Closed' ? 'text-success'
                  : ticket.status === 'Canceled' ? 'text-muted'
                  : 'text-info' // for Waiting for Approval
                }>
                  {ticket.status}
                </span>

              </td>
              <td>{ticket.priority}</td>
              {(user?.role === 'IT' || user?.role === 'Executive') && (              
                <td>{ticket.createdBy?.name.split(' ')[0] || 'Unknown'}</td>
              )}
              <td>{formatDate(ticket.createdAt)}</td>
              <td>{formatDate(ticket.updatedAt)}</td>
              <td>{ticket.handledBy?.name.split(' ')[0] || '—'}</td>
              {/* <td>{ticket.remark ? `${ticket.remark.split(' ').slice(0, 3).join(' ')} ...` : '—'}</td> */}
              <td>
                <Link to={`/tickets/${ticket._id}`} className="btn btn-sm btn-outline-primary me-2 mb-2">View</Link>
                  {ticket.status !== "Closed" && (
                    <>
                      <Link to={`/tickets/edit/${ticket._id}`} className="btn btn-sm btn-outline-secondary me-2 mb-2">Edit</Link>
                      {user?.role === 'IT' && (
                        <button className="btn btn-sm btn-outline-danger me-2" onClick={() => handleDelete(ticket._id)}>Delete</button>
                      )}
                    </>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
